import React, { useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme, GlobalStyles } from './theme';
import Header from './components/MainComponents/Header';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './components/MainComponents/Home';
import Login from './components/RoleMenagmentComponents/Login';
import Register from './components/UserComponents/Register';
import BookSearch from './components/BookComponents/BookSearch';
import AccessDenied from './components/RoleMenagmentComponents/AccessDenied';
import ProtectedRoute from './components/NavigationComponents/ProtectedRoute';
import { getCurrentUser, logout } from './services/AuthService';
import { useTheme } from "./hooks/useTheme";
import { ROLES } from './services/Constants';
import PasswordChangeRequest from './components/UserComponents/PasswordChangeRequest';
import PasswordChangeConfirm from './components/UserComponents/PasswordChangeConfirm';
import BorrowingHistory from './components/UserComponents/BorrowingHistory';
import UsersGrid from './components/UserComponents/UsersGrid';
import Notification from './components/UserComponents/Notification';
import { hasUnreadMessages } from './services/MessageService';
import PublicRoute from './components/NavigationComponents/PublicRoute';
import AddNewBook from './components/BookComponents/AddNewBook';
import AddNewEmployee from './components/EmployeeComponents/AddNewEmployee';
import EmployeeGrid from './components/EmployeeComponents/EmployeeGrid';
import ScheduleCalendar from './components/ScheduleComponents/ScheduleCalendar';
import BorrowBook from './components/BookComponents/BorrowBook';
import ReturnBook from './components/BookComponents/ReturnBook';
import LookupManagement from './components/AdminComponents/LookupManagement';
import Dashboard from './components/AdminComponents/Dashboard';
import BorrowingManagement from './components/AdminComponents/BorrowingManagement';
import GenreManagement from './components/AdminComponents/GenreManagement';
import CatalogManagement from './components/AdminComponents/CatalogManagement';
import RecommendationsPage from './components/BookComponents/RecommendationsPage';

function App() {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isDarkMode, toggleTheme] = useTheme();
  const [hasMessages, setHasMessages] = useState(false);
  useEffect(() => {
    async function fetchUser() {
      try {
        const userData = await getCurrentUser();

        if (userData.authenticated === false) {
          setUser(null);
        } else if (userData.authenticated === true) {
          setUser(userData);
          try {
            const unread = await hasUnreadMessages();
            setHasMessages(unread);
          } catch (err) {
            console.error("Greška pri provjeri poruka:", err);
            setHasMessages(false);
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error("Greška pri dohvaćanju korisnika:", err);
        setUser(null);
      } finally {
        setLoadingUser(false);
      }
    }

    fetchUser();
  }, []);

  const handleLogin = async (userData) => {
    setUser(userData);
    if (userData) {
      try {
        const unread = await hasUnreadMessages();
        setHasMessages(unread);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
    } catch (err) {
      console.error(err);
    }
  };

  if (loadingUser) {
    return (
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <GlobalStyles />
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p>Učitavanje...</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <GlobalStyles />
      <Router>
        <Header
          toggleTheme={toggleTheme}
          isDarkMode={isDarkMode}
          user={user}
          onLogout={handleLogout}
          hasMessages={hasMessages}
        />
        <Routes>
          <Route path="/" element={<Home user={user} />} />
          <Route path="/pretrazi" element={<BookSearch user={user} />} />
          <Route
            path="/passwordChangeRequest"
            element={<PasswordChangeRequest />}
          />
          <Route
            path="/passwordChangeConfirm"
            element={<PasswordChangeConfirm />}
          />

          <Route
            path="/prijava"
            element={
              <PublicRoute user={user}>
                <Login onLogin={handleLogin} />
              </PublicRoute>
            }
          />

          <Route
            path="/obavijesti"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.USER, ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <Notification user={user} onMessagesUpdate={setHasMessages} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/povijest"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.USER, ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <BorrowingHistory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/korisnici"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <UsersGrid />
              </ProtectedRoute>
            }
          />

          <Route
            path="/novaKnjiga"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <AddNewBook />
              </ProtectedRoute>
            }
          />

          <Route
            path="/posudbaKnjige"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <BorrowBook />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vracanjeKnjige"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <ReturnBook />
              </ProtectedRoute>
            }
          />

          <Route
            path="/registracija"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <Register user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/noviZaposlenik"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.ADMIN]}
              >
                <AddNewEmployee user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sviZaposlenici"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.ADMIN]}
              >
                <EmployeeGrid />
              </ProtectedRoute>
            }
          />

          <Route
            path="/raspored"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <ScheduleCalendar user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/postavke"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.ADMIN]}
              >
                <LookupManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/posudbePregled"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <BorrowingManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/zanrovi"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <GenreManagement user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/katalog"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <CatalogManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/preporuke"
            element={
              <ProtectedRoute
                user={user}
                loadingUser={loadingUser}
                allowedRoles={[ROLES.USER, ROLES.EMPLOYEE, ROLES.ADMIN]}
              >
                <RecommendationsPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route path="/accessDenied" element={<AccessDenied />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;

